'use client';

import { useId } from 'react';
import Link from "@/components/ui/Link";

interface OnesideTextProps {
  text: string;
  href?: string;
  background?: string;
  backgroundImage?: {
    url: string;
    enabled: boolean;
  };
  /** Text color override when using custom background */
  textColor?: string;
  /** Desktop font size for the title (explicit override) */
  fontSize?: string;
  /** Mobile font size for the title (explicit override). Falls back to the
   *  level-based mobile size when not provided. */
  fontSizeMobile?: string;
  /** Heading level (1-6) - determines font size if fontSize not provided */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Font weight */
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  /** Show divider below heading */
  showDivider?: boolean;
}

// Desktop font sizes based on heading level (h1-h6)
const HEADING_LEVEL_SIZES: Record<number, string> = {
  1: '32px',   // h1
  2: '26px',   // h2
  3: '22px',   // h3
  4: '20px',   // h4
  5: '17px',   // h5
  6: '14px',   // h6
};

// Mobile font sizes - smaller than desktop
const MOBILE_HEADING_LEVEL_SIZES: Record<number, string> = {
  1: '24px',   // h1
  2: '20px',   // h2
  3: '18px',   // h3
  4: '16px',   // h4
  5: '14px',   // h5
  6: '12px',   // h6
};

// Font weight mapping
const FONT_WEIGHT_MAP: Record<string, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

// Alignment mapping
const ALIGN_MAP: Record<string, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

export default function OnesideText({
  text,
  href,
  background,
  backgroundImage,
  textColor,
  fontSize,
  fontSizeMobile,
  level,
  align = 'left',
  fontWeight = 'bold',
  showDivider = false,
}: OnesideTextProps) {
  // Build background styles
  const containerStyle: React.CSSProperties = {};

  if (background) {
    containerStyle.backgroundColor = background;
  }

  if (backgroundImage?.enabled && backgroundImage?.url) {
    containerStyle.backgroundImage = `url(${backgroundImage.url})`;
    containerStyle.backgroundSize = 'cover';
    containerStyle.backgroundPosition = 'center';
    containerStyle.backgroundRepeat = 'no-repeat';
  }

  // Determine font size: explicit fontSize > level-based > default
  const desktopFontSize = fontSize || (level ? HEADING_LEVEL_SIZES[level] : undefined);
  const mobileFontSize =
    fontSizeMobile || (level ? MOBILE_HEADING_LEVEL_SIZES[level] : undefined);

  // Use custom text color or default blue
  const colorClass = textColor ? '' : 'text-215EF9';
  const fontWeightClass = FONT_WEIGHT_MAP[fontWeight] || 'font-bold';
  const alignClass = ALIGN_MAP[align] || 'justify-start';

  const uniqueId = useId();
  const stableId = `ost-${uniqueId.replace(/:/g, '')}`;

  const textStyle: React.CSSProperties = {
    ...(textColor ? { color: textColor } : {}),
    ...(desktopFontSize ? { fontSize: desktopFontSize } : {}),
  };

  // Use a semantic <hN> tag for section labels — search engines treat these as
  // hierarchy signals. Default to <h2> (page sections) so card titles inside
  // can stay as <h3>. Caller can override via the `level` prop (1–6).
  const HeadingTag = (`h${level ?? 2}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6');
  const content = (
    <div className={`flex items-center gap-2 ${alignClass} w-full`}>
      <HeadingTag className={`${stableId} ${fontWeightClass} leading-[1.4] ${colorClass}`} style={textStyle}>
        {text}
      </HeadingTag>
    </div>
  );

  return (
    <div className="w-full" style={containerStyle}>
      {mobileFontSize && (
        <style>{`@media(max-width:767px){.${stableId}{font-size:${mobileFontSize} !important}}`}</style>
      )}
      <div className="w-full">
        {href ? (
          <Link
            href={href}
            className={`flex items-center ${alignClass} px-4 py-3 hover:opacity-80 transition-colors duration-200 cursor-pointer`}
          >
            {content}
          </Link>
        ) : (
          <div className={`flex items-center ${alignClass} px-4 py-3`}>
            {content}
          </div>
        )}
        {showDivider && (
          <div className="border-b border-gray-300 mx-4" />
        )}
      </div>
    </div>
  );
}
